import React from 'react';
import { X } from 'lucide-react';
import clearOnEscape from './clearOnEscape';
import './Input.css';

/**
 * Baustein — Input.
 * Einzeiliges Textfeld; Eigentümer der Feld-Box-Optik (--bg-input, Border, Focus, Radius).
 * Wird von Form.Field umschlossen und überall genutzt, wo ein Textfeld nötig ist
 * (außer Filter-/Such-/Settings-Felder — die behalten ihr eigenes Design in
 * `FilterStrip.*` und `PageSettingsPopover.TextInput`).
 *
 * Beispiel:
 *   <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Deckname" />
 *   <Input size="sm" value={q} onChange={...} onClear={() => setQ('')} />
 *
 * Props:
 *   size     — 'md' (Standard) | 'sm' (kompakt)
 *   invalid  — boolean → rote Border (Fehlertext besitzt der umgebende Form.Field)
 *   onClear  — optional; zeigt ✕ sobald value nicht leer ist, Escape leert ebenso
 *              (geteiltes Tastatur-Verhalten aller Clear-Felder, siehe `ui/clearOnEscape.js`)
 *   …rest    — alle nativen <input>-Props (value, onChange, type, placeholder, disabled, …)
 */
const Input = React.forwardRef(function Input(
  { size = 'md', invalid = false, onClear, className = '', value, onKeyDown, ...rest },
  ref,
) {
  const classes = ['ui-input'];
  if (size !== 'md') classes.push(`ui-input--${size}`);
  if (invalid) classes.push('ui-input--invalid');
  if (className) classes.push(className);

  if (typeof onClear !== 'function') {
    return <input ref={ref} className={classes.join(' ')} value={value} onKeyDown={onKeyDown} {...rest} />;
  }

  const showClear = value != null && value !== '';
  return (
    <span className="ui-input-wrap">
      <input
        ref={ref}
        className={`${classes.join(' ')} ui-input--clearable`}
        value={value}
        {...clearOnEscape({ hasValue: showClear, clear: onClear, onKeyDown })}
        {...rest}
      />
      {showClear && (
        <button
          type="button"
          className="ui-input__clear"
          onClick={onClear}
          aria-label="Eingabe leeren"
        >
          <X size={14} aria-hidden />
        </button>
      )}
    </span>
  );
});

export default Input;
